import React from 'react';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline } from 'ionicons/icons';
import Button from '@shared/ui/Button';
import type {
  EligibleWorkspace,
  ResolveTrackingWorkspaceResult,
} from '../../pages/pro/resolveTrackingWorkspace';

export type TrackingDestinationStatus = ResolveTrackingWorkspaceResult['status'];

export interface ProTrackingDestinationLabelProps {
  status: TrackingDestinationStatus;
  workspace?: EligibleWorkspace | null;
  /** Eligible workspace count; when selected and > 1, the chevron affordance is emphasized. */
  eligibleCount?: number;
  /** Opens the workspace picker. Ignored when the label is non-interactive. */
  onPress?: () => void;
  disabled?: boolean;
  /**
   * When false, the label never opens the picker (context-bound destination).
   * Defaults to true whenever `onPress` is provided.
   */
  interactive?: boolean;
}

/**
 * Compact destination label for Pro tracking controls.
 *
 * - `selected` + workspace → "Tracking in: <name>" (tappable via onPress unless interactive={false})
 * - `needs-choice` → prompt to choose (tappable unless interactive={false})
 * - `none` / `context-ineligible` → short unavailable / explanation copy (not tappable)
 *
 * Does not own the picker; parent wires `onPress` → open `ProTrackingWorkspacePicker`.
 */
const ProTrackingDestinationLabel: React.FC<ProTrackingDestinationLabelProps> = ({
  status,
  workspace = null,
  eligibleCount = 0,
  onPress,
  disabled = false,
  interactive: interactiveProp = true,
}) => {
  const { t } = useTranslation('pro');

  let copy: string;
  let interactive = false;
  let ariaLabel: string | undefined;

  switch (status) {
    case 'selected': {
      copy = t('trackingWorkspace.trackingIn', {
        name: workspace?.name ?? '',
      });
      // Tappable when selected + onPress, unless context-locked via interactive={false}.
      interactive =
        interactiveProp && !disabled && typeof onPress === 'function';
      ariaLabel = t('trackingWorkspace.changeWorkspaceAria');
      break;
    }
    case 'needs-choice': {
      copy = t('trackingWorkspace.choosePrompt');
      interactive =
        interactiveProp && !disabled && typeof onPress === 'function';
      ariaLabel = t('trackingWorkspace.chooseWorkspaceAria');
      break;
    }
    case 'context-ineligible': {
      copy = t('trackingWorkspace.contextIneligibleHint');
      interactive = false;
      break;
    }
    case 'none':
    default: {
      copy = t('trackingWorkspace.noneHint');
      interactive = false;
      break;
    }
  }

  // Chevron when the user can open the picker; prefer showing it whenever
  // selected with multiple eligible or when a choice is still needed.
  const showChevron =
    interactive && (status === 'needs-choice' || eligibleCount !== 1);

  const baseClass = 'inline-flex items-center gap-1 text-xs max-w-full';
  const mutedClass = 'text-text-tertiary dark:text-text-dark-tertiary opacity-70';
  const activeClass = 'text-text-secondary dark:text-text-dark-secondary';

  if (interactive) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        noShadow
        onClick={onPress}
        ariaLabel={ariaLabel}
        className={`${baseClass} ${activeClass} px-0 py-0 h-auto min-h-0 gap-1 text-xs font-medium rounded-none shadow-none hover:bg-transparent dark:hover:bg-transparent active:opacity-70 focus:ring-0 focus:ring-offset-0`}
      >
        <span className="truncate">{copy}</span>
        {showChevron ? (
          <IonIcon icon={chevronDownOutline} className="text-sm shrink-0" aria-hidden />
        ) : null}
      </Button>
    );
  }

  return (
    <span
      className={`${baseClass} ${mutedClass}`}
      aria-disabled={
        status === 'none' || status === 'context-ineligible' || disabled
          ? true
          : undefined
      }
      title={
        status === 'none' || status === 'context-ineligible'
          ? t('trackingWorkspace.unavailable')
          : undefined
      }
    >
      <span className="truncate">{copy}</span>
    </span>
  );
};

export default ProTrackingDestinationLabel;
